import { PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { LoseSceneResult } from "../LoseSceneResult";
import { IDestroyOptions, Sprite, TextStyle } from "pixi.js";
import { FullScreenFade } from "../../../../common/components/FullScreenFade";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { LoseResourcesPackage } from "../resources/LoseResourcesPackage";
import { AdMultiplierComponent } from "../../../../common/components/AdMultiplierComponent";
import { LocalizationModel } from "../../../../common/models/LocalizationModel";
import { LocalizationKey } from "../../../../localization/Localization";
import { GameFont } from "../../../../common/GameFont";
import { LoseReasonView } from "../components/LoseReasonView";
import { LoseType } from "../LoseType";
import { TypedText } from "../../../../common/components/TypedText";
import { AdaptiveViewDrop } from "../../../../common/views/AdaptiveViewDrop";
import { UIButtonBase } from "../../../../common/components/UIButtonBase";
import { LosePopupController } from "../controllers/LosePopupController";

export class LoseView extends AdaptiveViewDrop<LoseSceneResult> {
    private fade: FullScreenFade;
    private header: Sprite;
    private headerText: TypedText;
    private loseReasonView: LoseReasonView;
    private adMultiplier: AdMultiplierComponent;
    private homeButton: UIButtonBase;
    private restartButton: UIButtonBase;

    public constructor(
        private readonly commonResources: CommonResourcesPackage,
        private readonly loseResources: LoseResourcesPackage,
        private readonly localizationModel: LocalizationModel,
        private readonly loseController: LosePopupController,
        protected loseType: LoseType,
        protected vegetablesForAd: number,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<LoseSceneResult>
    ) {
        super(canvasScaler, operationControls);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight, true);

        this.homeButton.onUp.add(this.onHomeButtonClick, this);
        this.restartButton.onUp.add(this.onRestartButtonClick, this);
        this.adMultiplier.onAdClick.add(this.onAdClick, this);
        this.adMultiplier.timeout.add(this.onAdTimeout, this);
    }

    public async start(): Promise<void> {
        await this.show();
        await new Promise((resolve) => setTimeout(resolve, 200));
        await Promise.all([this.headerText.animateText(), this.loseReasonView.animate()]);
        this.adMultiplier.startTimer();
    }

    protected override build(): void {
        this.cursor = "hover";
        this.fade = new FullScreenFade(this.canvasScaler, 0.8);
        this.frame = new Sprite(this.loseResources.assets_popups_lose_lose_frame_png);
        this.frame.alpha = 0;

        this.header = new Sprite(this.loseResources.assets_popups_lose_orange_ribbon_png);
        this.headerText = new TypedText(this.localizationModel, LocalizationKey.LevelFailed, new HeaderTextStyle(), 750, 115);
        this.headerText.position.y = 31;
        this.header.addChild(this.headerText);
        this.headerText.updateText(false);
        this.headerText.position.set((this.header.width - this.headerText.width) * 0.5, 26);

        this.loseReasonView = new LoseReasonView(this.loseResources, this.localizationModel, this.loseType);

        this.adMultiplier = new AdMultiplierComponent(this.commonResources, this.loseType == LoseType.Dead ? this.loseResources.assets_popups_lose_resurrect_icon_png : `+ ${this.vegetablesForAd}`, false, 1, 5000);

        this.homeButton = new UIButtonBase({ defaultTexture: this.commonResources.assets_common_button_background_png });
        const homeIcon = new Sprite(this.commonResources.assets_common_home_icon_png);
        homeIcon.position.set(40, 31);
        this.homeButton.defaultView.addChild(homeIcon);

        this.restartButton = new UIButtonBase({ defaultTexture: this.commonResources.assets_common_button_background_png });
        const restartIcon = new Sprite(this.commonResources.assets_common_restart_icon_png);
        restartIcon.position.set(46, 35);
        this.restartButton.defaultView.addChild(restartIcon);

        this.frame.addChild(
            this.header,
            this.loseReasonView,
            this.adMultiplier,
            this.homeButton,
            this.restartButton
        );

        this.addChild(
            this.fade,
            this.frame
        );

        super.build();
    }

    protected layout(width: number, height: number, _initialLayout: boolean = false): void {
        switch (height) {
            case 2400:
                this.layout9x20(width, height);
                break;
            case 1620:
                this.layout10x9();
                break;
            case 1800:
                this.layout4x3();
                break;
        }
        this.header.position.set(-124, -43);
        this.loseReasonView.position.set(this.loseType === LoseType.Dead ? 420 : 400, 240);
        this.adMultiplier.position.set(264, 439);
        this.homeButton.position.set(90, 606);
        this.restartButton.position.set(453, 606);

        this.circle?.position.set(width * 0.5, -40);
    }

    private layout9x20(width: number, height: number): void {
        this.frame.position.set((width - this.frame.width) * 0.5, (height - this.frame.height) * 0.5);
    }

    private layout10x9(): void {
        this.frame.position.set(1061, 351);
    }

    private layout4x3(): void {
        this.frame.position.set(821, 441);
    }

    private async onHomeButtonClick(): Promise<void> {
        await this.loseController.showMidrollAd();
        await this.hide();
        this.operationControls.complete(LoseSceneResult.Home)
    }

    private async onRestartButtonClick(): Promise<void> {
        await this.loseController.showMidrollAd();
        await this.hide();
        this.operationControls.complete(LoseSceneResult.Restart);
    }

    private async onAdClick(): Promise<void> {
        this.adMultiplier.disableTimer();
        const isAdViewed = await this.loseController.showAd();
        if (!isAdViewed) {
            return;
        }
        await this.hide();
        this.operationControls.complete(LoseSceneResult.Continue);
    }

    private onAdTimeout(): void {
        this.adMultiplier.disableTimer();
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.loseReasonView.cancellation.cancel();
        this.homeButton.onUp.remove(this.onHomeButtonClick, this);
        this.restartButton.onUp.remove(this.onRestartButtonClick, this);
        this.adMultiplier.onAdClick.remove(this.onAdClick, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 64,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#74007D",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
